/** Über mich: photo hero, then the workshop portrait beside the story in two
 *  text columns. */

import { FramedImage, OutlineButtonLink, PhotoHero } from '../components/ui'
import type { ContentBlock, PageContent } from '../lib/api'
import { useApi, useDocumentMeta, useHreflang, useLocale } from '../lib/hooks'
import { pagePaths, pathFor, t } from '../lib/i18n'

const HERO_BG = '/media/1111182C-38AA-4013-8635-AF018A7A9613_1_105_c.jpeg'

/** Pull a leading short line off a text block so it can render as the section
 *  heading, returning the remaining paragraphs. */
function splitLead(text: string): { lead: string; rest: string[] } {
  const lines = text.split('\n').filter(Boolean)
  if (lines.length && lines[0].length <= 30) {
    return { lead: lines[0], rest: lines.slice(1) }
  }
  return { lead: '', rest: lines }
}

export default function UeberMich() {
  const locale = useLocale()
  const strings = t(locale)
  const { data } = useApi<PageContent>(`/api/pages/uebermich?locale=${locale}`)

  useDocumentMeta(
    data?.title || strings.meta.uebermich.title,
    strings.meta.uebermich.description,
  )
  useHreflang(pagePaths('uebermich'))

  const blocks: ContentBlock[] = data?.blocks ?? []
  const hero = blocks.find((b) => b.type === 'hero')
  const portrait = blocks.find((b) => b.type === 'image')
  const texts = blocks.filter((b) => b.type === 'text')
  const col1 = splitLead(texts[0]?.text ?? '')
  const col2 = (texts[1]?.text ?? '').split('\n').filter(Boolean)

  return (
    <>
      <PhotoHero
        image={HERO_BG}
        title={hero?.eyebrow || strings.meta.uebermich.heading}
        subtitle={hero?.headline}
      />

      <section className="bg-paper px-6 py-16 md:px-10 md:py-24" data-testid="about-body">
        <div className="mx-auto grid max-w-[1200px] grid-cols-1 gap-12 lg:grid-cols-[minmax(0,4fr)_minmax(0,8fr)] lg:gap-20">
          <div>
            {portrait?.src ? (
              <FramedImage
                filename={portrait.src.replace(/^\/media\//, '')}
                alt={portrait.alt || 'Zvi Dori in seiner Werkstatt'}
                sizes="(max-width: 1024px) 100vw, 400px"
              />
            ) : null}
          </div>

          <div>
            {col1.lead ? (
              <h2 className="rule m-0 mb-10 font-serif text-[clamp(32px,4vw,46px)] font-medium leading-[1.1] text-ink">
                {col1.lead}
              </h2>
            ) : null}

            <div className="grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-14">
              <div className="font-sans text-[18px] leading-[1.65] text-ink">
                {col1.rest.map((line, index) => (
                  <p key={index} className="m-0 mb-[1.1em] last:mb-0">
                    {line}
                  </p>
                ))}
              </div>
              <div className="font-sans text-[18px] leading-[1.65] text-ink">
                {col2.map((line, index) => (
                  <p key={index} className="m-0 mb-[1.1em] last:mb-0">
                    {line}
                  </p>
                ))}
                <div className="mt-10">
                  <OutlineButtonLink to={pathFor(locale, 'galerie')}>{strings.nav.galerie}</OutlineButtonLink>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
